import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleRounded,
  DownloadRounded,
  ErrorRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  InfoRounded,
  RefreshRounded,
  WarningAmberRounded,
} from '@mui/icons-material';

import {
  downloadMissingDataWorkbook,
  downloadPlantDataNoticeCsv,
  getPlantDataReview,
} from '../services/industrialCostService';
import type {
  PlantDataNotice,
  PlantDataNoticeAffected,
  PlantDataNoticeLevel,
  PlantDataNoticeTypeTexts,
  PlantDataReviewFile,
} from '../services/industrialCostService';
import { formatDataDay } from '../utils/dailyLoadRunFile';
import {
  affectedSummaryText,
  filterNotices,
  groupFileNotices,
  noticeTypeCsv,
  sortNotices,
} from '../utils/noticeGroups';
import type { NoticeSortKey, NoticeTypeGroup } from '../utils/noticeGroups';
import { useTableSort } from '../utils/tableSort';
import SortableHeaderCell from '../components/SortableHeaderCell';
import { MarkHolidayButton } from './IndustrialCostPlantProductionYear';

// IND-022: un día laborable sin producción se puede marcar como festivo.
function holidayDay(notice: PlantDataNotice): string | null {
  return notice.code === 'dia_laborable_sin_produccion' ? notice.subject?.key ?? null : null;
}

import { formatEsNumber } from '../utils/numberFormat';
// IC-STOCK-09 / IND-012: informe «Revisa tus ficheros». Cada aviso dice qué
// dato falla, qué eslabón falta, todo lo afectado y qué hacer. Solo informa.

export const PLANT_DATA_REVIEW_QUERY_KEY = ['industrial-cost-plant-data-review'];

export const LEVEL_STYLE: Record<PlantDataNoticeLevel, { color: string; label: string; muiColor: 'error' | 'warning' | 'info' }> = {
  blocks: { color: '#f87171', label: 'Bloquea', muiColor: 'error' },
  incomplete: { color: '#fbbf24', label: 'Cifra incompleta', muiColor: 'warning' },
  info: { color: '#7dd3fc', label: 'Informativo', muiColor: 'info' },
};

const SUMMARY_SIZE = 5;

export function noticeAnchorId(id: string): string {
  return `aviso-${encodeURIComponent(id)}`;
}

const OPEN_NOTICE_EVENT = 'proa:open-plant-notice';

export function scrollToNotice(id: string) {
  // El informe abre el fichero del aviso y después baja hasta él.
  window.dispatchEvent(new CustomEvent(OPEN_NOTICE_EVENT, { detail: id }));
  if (!document.getElementById('revisa-tus-ficheros')) {
    // Si «Revisa tus ficheros» no está en esta pantalla, se abre por el enlace.
    window.location.hash = 'revisa-tus-ficheros';
  }
}

function LevelIcon({ level }: { level: PlantDataNoticeLevel }) {
  if (level === 'blocks') return <ErrorRounded sx={{ color: LEVEL_STYLE.blocks.color }} />;
  if (level === 'incomplete') return <WarningAmberRounded sx={{ color: LEVEL_STYLE.incomplete.color }} />;
  return <InfoRounded sx={{ color: LEVEL_STYLE.info.color }} />;
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function saveNoticeCsv(notice: PlantDataNotice) {
  saveBlob(await downloadPlantDataNoticeCsv(notice.id), `afectados-${notice.code}.csv`);
}

export function affectedText(item: PlantDataNoticeAffected): string {
  const parts = [
    item.document_type ? `${item.document_type}:` : '',
    item.reference,
    item.quantity != null
      ? `${formatEsNumber(item.quantity, { maximumFractionDigits: 2 })}${item.unit ? ` ${item.unit}` : ''}`
      : null,
    formatDataDay(item.date),
    item.excel_row != null ? `fila ${item.excel_row}` : null,
    item.detail,
  ].filter(Boolean);
  return parts.join(' · ').replace(': · ', ': ');
}

export function NoticeCard({ notice, compact = false }: { notice: PlantDataNotice; compact?: boolean }) {
  const [showAll, setShowAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const style = LEVEL_STYLE[notice.level];
  const shown = showAll ? notice.affected : notice.affected.slice(0, SUMMARY_SIZE);

  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await saveNoticeCsv(notice);
    } catch {
      setDownloadError('No se pudo descargar la lista. Pulsa «Revisar de nuevo» y vuelve a intentarlo.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      id={noticeAnchorId(notice.id)}
      sx={{
        border: `1px solid ${style.color}55`,
        borderLeft: `4px solid ${style.color}`,
        borderRadius: 2,
        p: 1.5,
        bgcolor: 'rgba(2,6,23,0.35)',
        scrollMarginTop: 80,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <LevelIcon level={notice.level} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: 14 }}>{notice.title}</Typography>
            <Chip size="small" color={style.muiColor} variant="outlined" label={style.label} />
            {/* IND-020: cliente nuevo (sale arriba). */}
            {notice.is_new ? <Chip size="small" color="info" label="Nuevo" /> : null}
            {notice.affected_count > 0 ? (
              <Chip size="small" variant="outlined" label={`${formatEsNumber(notice.affected_count)} afectados`} />
            ) : null}
          </Stack>
          <Typography sx={{ color: '#cbd5e1', fontSize: 13, mt: 0.5 }}>{notice.missing_link}.</Typography>
          <Typography sx={{ color: '#e2e8f0', fontSize: 13, mt: 0.5 }}>
            <Box component="span" sx={{ fontWeight: 800 }}>Qué hacer: </Box>
            {notice.action}
          </Typography>
          {!compact && shown.length > 0 ? (
            <Stack spacing={0.25} sx={{ mt: 1, maxHeight: showAll ? 360 : 'none', overflowY: 'auto' }}>
              {shown.map((item, index) => (
                <Typography key={index} sx={{ color: '#94a3b8', fontSize: 12 }}>
                  {affectedText(item)}
                </Typography>
              ))}
            </Stack>
          ) : null}
          {!compact ? (
            <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
              {notice.affected.length > SUMMARY_SIZE ? (
                <Button size="small" sx={{ textTransform: 'none', px: 0 }} onClick={() => setShowAll((v) => !v)}>
                  {showAll ? 'Ver menos' : `Ver los ${notice.affected.length} afectados`}
                </Button>
              ) : null}
              {notice.affected_count > notice.affected.length ? (
                <Typography sx={{ color: '#64748b', fontSize: 12, alignSelf: 'center' }}>
                  {`Se muestran ${notice.affected.length} de ${notice.affected_count}.`}
                </Typography>
              ) : null}
              {notice.affected_count > 0 ? (
                <Button
                  size="small"
                  startIcon={downloading ? <CircularProgress size={12} /> : <DownloadRounded />}
                  sx={{ textTransform: 'none' }}
                  onClick={() => void handleDownload()}
                  disabled={downloading}
                >
                  Descargar lista completa
                </Button>
              ) : null}
              {holidayDay(notice) ? <MarkHolidayButton day={holidayDay(notice)!} /> : null}
            </Stack>
          ) : null}
          {compact && holidayDay(notice) ? <MarkHolidayButton day={holidayDay(notice)!} /> : null}
          {downloadError ? (
            <Typography sx={{ color: '#f87171', fontSize: 12 }}>{downloadError}</Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

// IND-021: detalle de una fila de la tabla (lo que mostraba cada tarjeta).
function NoticeRowDetail({ notice }: { notice: PlantDataNotice }) {
  const [showAll, setShowAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const negativeStock = notice.code === 'stock_negativo';
  // En stock negativo el propio material no es un afectado: son sus consumidores.
  const affected = negativeStock
    ? notice.affected.filter((item) => item.document_type !== 'material')
    : notice.affected;
  const materialLine = negativeStock ? notice.affected.find((item) => item.document_type === 'material') : null;
  const shown = showAll ? affected : affected.slice(0, SUMMARY_SIZE);
  const total = negativeStock ? Math.max(notice.affected_count - (materialLine ? 1 : 0), 0) : notice.affected_count;

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      await saveNoticeCsv(notice);
    } catch {
      setError('No se pudo descargar la lista. Pulsa «Revisar de nuevo» y vuelve a intentarlo.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ py: 1, px: 1.5, bgcolor: 'rgba(15,23,42,0.6)', borderLeft: `3px solid ${LEVEL_STYLE[notice.level].color}` }}>
      {negativeStock ? (
        <Typography sx={{ color: '#e2e8f0', fontSize: 12.5 }}>
          <Box component="span" sx={{ fontWeight: 800 }}>Por qué: </Box>
          {notice.missing_link}.{materialLine?.detail ? ` ${materialLine.detail}.` : ''}
        </Typography>
      ) : null}
      <Typography sx={{ color: '#e2e8f0', fontSize: 12.5, mt: negativeStock ? 0.5 : 0 }}>
        <Box component="span" sx={{ fontWeight: 800 }}>Qué hacer: </Box>
        {notice.action}
      </Typography>
      {shown.length > 0 ? (
        <>
          <Typography sx={{ color: '#94a3b8', fontSize: 11.5, fontWeight: 800, mt: 0.75 }}>
            {negativeStock ? 'Productos que lo consumen desde el recuento:' : 'Afectados:'}
          </Typography>
          <Stack spacing={0.25} sx={{ maxHeight: showAll ? 300 : 'none', overflowY: 'auto' }}>
            {shown.map((item, index) => (
              <Typography key={index} sx={{ color: '#94a3b8', fontSize: 12 }}>
                {affectedText(item)}
              </Typography>
            ))}
          </Stack>
        </>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap alignItems="center">
        {affected.length > SUMMARY_SIZE ? (
          <Button size="small" sx={{ textTransform: 'none', px: 0 }} onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Ver menos' : `Ver los ${formatEsNumber(affected.length)}`}
          </Button>
        ) : null}
        {total > affected.length ? (
          <Typography sx={{ color: '#64748b', fontSize: 12 }}>
            {`Se muestran ${formatEsNumber(affected.length)} de ${formatEsNumber(total)}.`}
          </Typography>
        ) : null}
        {notice.affected_count > 0 ? (
          <Button
            size="small"
            startIcon={downloading ? <CircularProgress size={12} /> : <DownloadRounded />}
            sx={{ textTransform: 'none' }}
            onClick={() => void handleDownload()}
            disabled={downloading}
          >
            Descargar lista completa
          </Button>
        ) : null}
        {holidayDay(notice) ? <MarkHolidayButton day={holidayDay(notice)!} /> : null}
      </Stack>
      {error ? <Typography sx={{ color: '#f87171', fontSize: 12 }}>{error}</Typography> : null}
    </Box>
  );
}

const SORT_COLUMNS: Array<{ key: NoticeSortKey; label: string }> = [
  { key: 'element', label: 'Elemento' },
  { key: 'what', label: 'Qué pasa' },
  { key: 'affects', label: 'Afecta a' },
  { key: 'new', label: 'Nuevo' },
];
const TABLE_ROW_HEIGHT = 44;
const TABLE_VISIBLE_ROWS = 10;

// IND-021: tabla compacta de un tipo: buscador, orden por columna, 10 filas
// visibles con scroll y detalle de la fila pulsada.
function NoticeTypeTable({
  group,
  focus,
  ready,
}: {
  group: NoticeTypeGroup;
  focus: { id: string; nonce: number } | null;
  ready: boolean;
}) {
  const [search, setSearch] = useState('');
  const sort = useTableSort<NoticeSortKey>('affects', 'desc');
  const [openId, setOpenId] = useState<string | null>(null);
  const showNew = group.newCount > 0;
  const columns = SORT_COLUMNS.filter((column) => column.key !== 'new' || showNew);

  const rows = useMemo(
    () => sortNotices(filterNotices(group.notices, search), sort.key, sort.direction),
    [group.notices, search, sort.key, sort.direction],
  );

  // Un enlace a un aviso: limpia el buscador, abre su fila y baja hasta ella
  // una vez pintada y con el fichero ya abierto (también si está más allá de
  // las 10 visibles). Cada enlace se atiende una sola vez.
  const handledFocus = useRef<number | null>(null);
  const scrolledFocus = useRef<number | null>(null);
  useEffect(() => {
    if (!focus || handledFocus.current === focus.nonce) return;
    if (!group.notices.some((notice) => notice.id === focus.id)) return;
    handledFocus.current = focus.nonce;
    setSearch('');
    setOpenId(focus.id);
  }, [focus, group.notices]);
  useEffect(() => {
    if (!focus || !ready || openId !== focus.id || search) return;
    if (scrolledFocus.current === focus.nonce) return;
    scrolledFocus.current = focus.nonce;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(noticeAnchorId(focus.id))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focus, ready, openId, search]);

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        size="small"
        placeholder="Buscar por código o nombre"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 0.75, width: { xs: '100%', sm: 320 }, '& input': { fontSize: 13 } }}
      />
      <Box
        sx={{
          maxHeight: TABLE_ROW_HEIGHT * (TABLE_VISIBLE_ROWS + 1),
          overflowY: 'auto',
          border: '1px solid rgba(148,163,184,0.16)',
          borderRadius: 1.5,
        }}
      >
        <Table size="small" stickyHeader sx={{ '& td, & th': { borderColor: 'rgba(148,163,184,0.12)' } }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <SortableHeaderCell
                  key={column.key}
                  label={column.label}
                  columnKey={column.key}
                  sort={sort}
                  firstDirection={column.key === 'affects' || column.key === 'new' ? 'desc' : 'asc'}
                  sx={{ bgcolor: '#0f172a', color: '#94a3b8', fontSize: 12, py: 0.75 }}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ color: '#64748b', fontSize: 12 }}>
                  Ningún aviso coincide con la búsqueda.
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((notice) => {
              const open = openId === notice.id;
              return (
                <Fragment key={notice.id}>
                  <TableRow
                    id={noticeAnchorId(notice.id)}
                    hover
                    selected={open}
                    onClick={() => setOpenId(open ? null : notice.id)}
                    sx={{ cursor: 'pointer', height: TABLE_ROW_HEIGHT, scrollMarginTop: 48 }}
                  >
                    <TableCell sx={{ color: '#f8fafc', fontSize: 12.5, fontWeight: 700, maxWidth: 220 }}>
                      {notice.subject?.label || notice.title}
                    </TableCell>
                    <TableCell sx={{ color: '#cbd5e1', fontSize: 12, maxWidth: 0, width: '50%' }}>
                      <Typography noWrap title={notice.missing_link} sx={{ fontSize: 12 }}>
                        {notice.missing_link}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#e2e8f0', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {affectedSummaryText(notice.affected_summary)}
                    </TableCell>
                    {showNew ? (
                      <TableCell>
                        {notice.is_new ? <Chip size="small" color="info" label="Nuevo" /> : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                  {open ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ p: 0 }}>
                        <NoticeRowDetail notice={notice} />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

// IND-021: una fila por tipo de aviso del fichero.
function NoticeTypeRow({
  group,
  open,
  onToggle,
  focus,
  ready,
}: {
  group: NoticeTypeGroup;
  open: boolean;
  onToggle: () => void;
  focus: { id: string; nonce: number } | null;
  ready: boolean;
}) {
  const style = LEVEL_STYLE[group.level];
  const count = group.notices.length;
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadMissing = async () => {
    if (!group.missingTable) return;
    setDownloading(true);
    setError(null);
    try {
      saveBlob(await downloadMissingDataWorkbook(group.missingTable), `lo_que_falta_${group.missingTable}.xlsx`);
    } catch {
      setError('No se pudo descargar. Vuelve a intentarlo.');
    } finally {
      setDownloading(false);
    }
  };
  const downloadList = () => {
    const blob = new Blob(['\ufeff' + noticeTypeCsv(group)], { type: 'text/csv;charset=utf-8' });
    saveBlob(blob, `avisos-${group.code}.csv`);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${style.color}55`,
        borderLeft: `4px solid ${style.color}`,
        borderRadius: 2,
        p: 1.5,
        bgcolor: 'rgba(2,6,23,0.35)',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <LevelIcon level={group.level} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: 14 }}>
              {`${group.title} · ${formatEsNumber(count)}`}
            </Typography>
            <Chip size="small" color={style.muiColor} variant="outlined" label={style.label} />
            {group.newCount > 0 ? (
              <Chip size="small" color="info" label={`${formatEsNumber(group.newCount)} ${group.newCount === 1 ? 'nuevo' : 'nuevos'}`} />
            ) : null}
          </Stack>
          <Typography sx={{ color: '#cbd5e1', fontSize: 13, mt: 0.5 }}>
            {group.what.replace(/\.$/, '')}.{group.totalText ? ` ${group.totalText}.` : ''}
          </Typography>
          <Typography sx={{ color: '#e2e8f0', fontSize: 13, mt: 0.5 }}>
            <Box component="span" sx={{ fontWeight: 800 }}>Qué hacer: </Box>
            {group.todo}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={onToggle}>
              {open
                ? `Ocultar ${count === 1 ? 'el 1' : `los ${formatEsNumber(count)}`}`
                : `Ver ${count === 1 ? 'el 1' : `los ${formatEsNumber(count)}`}`}
            </Button>
            {group.missingTable ? (
              <Button
                size="small"
                startIcon={downloading ? <CircularProgress size={12} /> : <DownloadRounded />}
                sx={{ textTransform: 'none' }}
                onClick={() => void downloadMissing()}
                disabled={downloading}
              >
                Descargar lo que falta
              </Button>
            ) : null}
            <Button size="small" startIcon={<DownloadRounded />} sx={{ textTransform: 'none' }} onClick={downloadList}>
              Descargar lista completa
            </Button>
          </Stack>
          {error ? <Typography sx={{ color: '#f87171', fontSize: 12 }}>{error}</Typography> : null}
          {/* La tabla solo se monta al pulsar «Ver los N». */}
          {open ? <NoticeTypeTable group={group} focus={focus} ready={ready} /> : null}
        </Box>
      </Stack>
    </Box>
  );
}

function FileRow({
  file,
  noticesById,
  noticeTypes,
  focus,
}: {
  file: PlantDataReviewFile;
  noticesById: Map<string, PlantDataNotice>;
  noticeTypes: Record<string, PlantDataNoticeTypeTexts> | undefined;
  focus: { id: string; nonce: number } | null;
}) {
  const own = useMemo(
    () => (file.notice_ids ?? []).map((id) => noticesById.get(id)).filter(Boolean) as PlantDataNotice[],
    [file, noticesById],
  );
  const related = (file.related_notice_ids ?? [])
    .map((id) => noticesById.get(id))
    .filter(Boolean) as PlantDataNotice[];
  const [open, setOpen] = useState(own.some((notice) => notice.level === 'blocks'));
  // El fichero ha terminado de abrirse (para bajar a una fila enlazada).
  const [entered, setEntered] = useState(open);
  const hasDetail = own.length > 0 || related.length > 0;
  // IND-021: un tipo por código, con memo.
  const groups = useMemo(() => groupFileNotices(own, noticeTypes), [own, noticeTypes]);
  const [openTypes, setOpenTypes] = useState<Set<string>>(new Set());
  // Un enlace a un aviso de este fichero abre el fichero y su tipo (una vez:
  // recargar los datos no lo repite).
  const handledFocus = useRef<number | null>(null);
  useEffect(() => {
    if (!focus || handledFocus.current === focus.nonce) return;
    handledFocus.current = focus.nonce;
    setOpen(true);
    const owner = groups.find((group) => group.notices.some((notice) => notice.id === focus.id));
    if (owner) setOpenTypes((current) => new Set(current).add(owner.key));
  }, [focus, groups]);
  const toggleType = (key: string) =>
    setOpenTypes((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const counts = {
    blocks: own.filter((n) => n.level === 'blocks').length,
    incomplete: own.filter((n) => n.level === 'incomplete').length,
    info: own.filter((n) => n.level === 'info').length,
  };

  return (
    <Box sx={{ border: '1px solid rgba(148,163,184,0.16)', borderRadius: 2, bgcolor: 'rgba(2,6,23,0.35)' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ p: 1.5, cursor: hasDetail ? 'pointer' : 'default' }}
        onClick={() => hasDetail && setOpen((value) => !value)}
      >
        {counts.blocks > 0 ? (
          <ErrorRounded sx={{ color: LEVEL_STYLE.blocks.color }} />
        ) : counts.incomplete > 0 ? (
          <WarningAmberRounded sx={{ color: LEVEL_STYLE.incomplete.color }} />
        ) : (
          <CheckCircleRounded sx={{ color: '#4ade80' }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: 14 }}>{file.label}</Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>{file.file_name}</Typography>
        </Box>
        {(Object.keys(counts) as PlantDataNoticeLevel[]).map((level) =>
          counts[level] > 0 ? (
            <Chip key={level} size="small" color={LEVEL_STYLE[level].muiColor} label={`${formatEsNumber(counts[level])} · ${LEVEL_STYLE[level].label}`} />
          ) : null,
        )}
        {!hasDetail ? (
          <Chip size="small" sx={{ color: '#4ade80' }} label="Todo bien" variant="outlined" />
        ) : open ? (
          <ExpandLessRounded sx={{ color: '#94a3b8' }} />
        ) : (
          <ExpandMoreRounded sx={{ color: '#94a3b8' }} />
        )}
      </Stack>

      <Collapse in={open} onEntered={() => setEntered(true)} onExit={() => setEntered(false)}>
        <Stack spacing={1} sx={{ px: 1.5, pb: 1.5 }}>
          {groups.map((group) => (
            <NoticeTypeRow
              key={group.key}
              group={group}
              open={openTypes.has(group.key)}
              onToggle={() => toggleType(group.key)}
              focus={focus}
              ready={entered}
            />
          ))}
          {related.length > 0 ? (
            <Box>
              <Typography sx={{ color: '#94a3b8', fontSize: 12, fontWeight: 800 }}>
                También afecta a este fichero (se corrige en otro):
              </Typography>
              {related.map((notice) => (
                <Button
                  key={notice.id}
                  size="small"
                  sx={{ textTransform: 'none', justifyContent: 'flex-start', display: 'block', textAlign: 'left' }}
                  onClick={() => scrollToNotice(notice.id)}
                >
                  {`${LEVEL_STYLE[notice.level].label} · ${notice.title}`}
                </Button>
              ))}
            </Box>
          ) : null}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function IndustrialCostFileReviewPanel() {
  const reviewQuery = useQuery({
    queryKey: PLANT_DATA_REVIEW_QUERY_KEY,
    queryFn: getPlantDataReview,
    staleTime: 60_000,
    retry: false,
  });

  const data = reviewQuery.data;
  const noticesById = useMemo(
    () => new Map((data?.notices ?? []).map((notice) => [notice.id, notice])),
    [data],
  );

  // IND-004: el enlace del Panel y de la carga diaria lleva hasta aquí.
  useEffect(() => {
    if (!data || window.location.hash !== '#revisa-tus-ficheros') return;
    document.getElementById('revisa-tus-ficheros')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }, [data]);

  const counts = data?.counts;

  // IND-012 / IND-021: un enlace a un aviso abre su fichero, su tipo y su
  // fila; la tabla baja hasta ella cuando la ha pintado.
  const [focusRequest, setFocusRequest] = useState<{ fileKey: string; id: string; nonce: number } | null>(null);
  useEffect(() => {
    const handler = (event: Event) => {
      const id = (event as CustomEvent<string>).detail;
      const owner = (data?.files ?? []).find((file) => (file.notice_ids ?? []).includes(id));
      if (owner) {
        setFocusRequest((current) => ({ fileKey: owner.file_key, id, nonce: (current?.nonce ?? 0) + 1 }));
      }
    };
    window.addEventListener(OPEN_NOTICE_EVENT, handler);
    return () => window.removeEventListener(OPEN_NOTICE_EVENT, handler);
  }, [data]);
  const fileFocus = useMemo(
    () => (focusRequest ? { id: focusRequest.id, nonce: focusRequest.nonce } : null),
    [focusRequest],
  );

  return (
    <Paper
      id="revisa-tus-ficheros"
      sx={{
        borderRadius: 4,
        bgcolor: 'rgba(15,23,42,0.72)',
        border: '1px solid rgba(148,163,184,0.16)',
      }}
    >
      <Stack spacing={1.5} sx={{ p: 2.5 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography sx={{ color: '#7dd3fc', fontSize: 12, fontWeight: 950, letterSpacing: 0.8 }}>
              4 · REVISA TUS FICHEROS
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
              Qué no se ha podido usar, qué falta, todo lo afectado y qué corregir en cada fichero.
              Primero lo que bloquea; dentro de cada nivel, lo que afecta a más documentos.
            </Typography>
            {counts ? (
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                {(Object.keys(LEVEL_STYLE) as PlantDataNoticeLevel[]).map((level) => (
                  <Chip
                    key={level}
                    size="small"
                    color={LEVEL_STYLE[level].muiColor}
                    variant={counts[level] > 0 ? 'filled' : 'outlined'}
                    label={`${counts[level] ?? 0} · ${LEVEL_STYLE[level].label}`}
                  />
                ))}
              </Stack>
            ) : null}
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={reviewQuery.isFetching ? <CircularProgress size={14} /> : <RefreshRounded />}
            onClick={() => void reviewQuery.refetch()}
            disabled={reviewQuery.isFetching}
            sx={{ textTransform: 'none' }}
          >
            Revisar de nuevo
          </Button>
        </Stack>

        {reviewQuery.isLoading ? (
          <Alert severity="info" variant="outlined">
            Revisando los ficheros cargados…
          </Alert>
        ) : reviewQuery.isError ? (
          (reviewQuery.error as { response?: { status?: number } })?.response?.status === 403 ? (
            <Alert severity="info" variant="outlined">
              Para ver este informe necesitas permiso de gestión de inventario.
            </Alert>
          ) : (
            <Alert severity="error" variant="outlined">
              No se pudo generar el informe de revisión.
            </Alert>
          )
        ) : data ? (
          <>
            {(data.notices ?? []).length === 0 ? (
              <Alert severity="success" variant="outlined">
                No se ha encontrado nada que revisar en los ficheros cargados.
              </Alert>
            ) : null}
            <Stack spacing={1}>
              {data.files.map((file) => (
                <FileRow
                  key={file.file_key}
                  file={file}
                  noticesById={noticesById}
                  noticeTypes={data.notice_types}
                  focus={focusRequest?.fileKey === file.file_key ? fileFocus : null}
                />
              ))}
            </Stack>
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}
